import { z } from 'zod';

import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import {
  PortalDialog,
  PortalDialogContent,
  PortalDialogDescription,
  PortalDialogFooter,
  PortalDialogHeader,
  PortalDialogTitle,
} from '@/modules/ui/components/portal-dialog/PortalDialog';
import { useInviteUserMutation } from '@/modules/users/mutations/useInviteUserMutation';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

const inviteFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your teammate’s work email.')
    .email('Enter a work email like name@company.com.'),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

interface InviteTeammateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The one-field invite. Company and role are auto-populated server-side
 * from the inviter's context, and no invite email is sent: the teammate
 * signs in with this address. The form remounts with the dialog, so a
 * closed dialog never reopens on a stale draft or error. The teammate
 * appearing in the list confirms the invite; a failure stays in the dialog.
 */
export function InviteTeammateDialog({
  open,
  onOpenChange,
}: InviteTeammateDialogProps) {
  const inviteUserMutation = useInviteUserMutation();

  const changeOpen = (next: boolean) => {
    if (!next) inviteUserMutation.reset();
    onOpenChange(next);
  };

  const handleSubmit = (values: InviteFormValues) => {
    inviteUserMutation.mutate(values, {
      onSuccess: () => changeOpen(false),
    });
  };

  return (
    <PortalDialog onOpenChange={changeOpen} open={open}>
      <PortalDialogContent>
        {/* onBlur mode would flag the empty field as required when the user
            clicks Cancel or the close button; the error pushes the footer
            down, which moves Cancel away and swallows the click. */}
        <Form
          defaultValues={{ email: '' }}
          mode="onSubmit"
          noValidate
          onSubmit={handleSubmit}
          schema={inviteFormSchema}
        >
          <div className="flex flex-col gap-5">
            <PortalDialogHeader>
              <PortalDialogTitle>Invite a teammate</PortalDialogTitle>
              <PortalDialogDescription>
                They sign in with this email. We don’t send an invite, so let
                them know they’re in. From then on, they can use Finlay right
                from Slack.
              </PortalDialogDescription>
            </PortalDialogHeader>
            <FormInput
              autoFocus
              label="Work email"
              name="email"
              placeholder="teammate@company.com"
              type="email"
            />
            {inviteUserMutation.isError && (
              <InlineError>
                {actionErrorMessage(
                  `invite ${inviteUserMutation.variables.email}`,
                  inviteUserMutation.error,
                )}
              </InlineError>
            )}
            <PortalDialogFooter>
              <Button
                className="h-8 rounded-md"
                onClick={() => changeOpen(false)}
                size="sm"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                className="h-8 rounded-md"
                loading={inviteUserMutation.isPending}
                size="sm"
                type="submit"
              >
                Invite
              </Button>
            </PortalDialogFooter>
          </div>
        </Form>
      </PortalDialogContent>
    </PortalDialog>
  );
}
